import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, DataTable, StatusBadge, Sel, BranchSel, Dot, Field, Kpi, opts } from "@/components/kit";
import DeviceLogs from "@/components/DeviceLogs";
import { api, useApi, fmtErr, dt, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["barrier", "lpr_camera", "qr_scanner", "rfid_reader", "payment_terminal", "network", "internet", "loop_detector", "sensor", "emergency_release", "cctv", "power_supply"];

function AddDevice({ onClose, onSaved }) {
  const { branches } = useAuth();
  const [f, setF] = useState({ branch_id: branches[0]?.id, lane_id: "", type: "lpr_camera", device_code: "" });
  const lanes = branches.find((b) => b.id === f.branch_id)?.lanes || [];
  const save = async () => {
    try {
      const { data } = await api.post("/devices", { ...f, lane_id: f.lane_id || null });
      toast.success(`Device created. API key (shown once): ${data.device_api_key}`, { duration: 15000 });
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card" data-testid="device-add-dialog">
        <DialogHeader><DialogTitle>Register device</DialogTitle></DialogHeader>
        <Field label="Branch"><BranchSel value={f.branch_id} onChange={(v) => setF({ ...f, branch_id: v, lane_id: "" })} all={false} testid="device-branch" className="w-full" /></Field>
        <Field label="Lane"><Sel value={f.lane_id} onChange={(v) => setF({ ...f, lane_id: v })} allLabel="No lane (branch-level)" options={lanes.map((l) => ({ value: l.id, label: l.name }))} testid="device-lane" className="w-full" /></Field>
        <Field label="Type"><Sel value={f.type} onChange={(v) => setF({ ...f, type: v })} options={opts(TYPES)} testid="device-type" className="w-full" /></Field>
        <Field label="Device code"><Input data-testid="device-code" value={f.device_code} onChange={(e) => setF({ ...f, device_code: e.target.value.toUpperCase() })} placeholder="MKT-EN3-LPR" /></Field>
        <Field label="IP / API reference"><Input data-testid="device-ip" value={f.ip_address || ""} onChange={(e) => setF({ ...f, ip_address: e.target.value })} placeholder="10.10.1.50" /></Field>
        <Button data-testid="device-save" onClick={save} disabled={!f.device_code}>Register</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Devices() {
  const { branch, setBranch, can, branchName } = useAuth();
  const [f, setF] = useState({ type: "", status: "" });
  const [logs, setLogs] = useState(null);
  const [add, setAdd] = useState(false);
  const { data, loading, reload } = useApi("/devices", { ...f, branch_id: branch });
  const upd = async (d, body, msg) => {
    try {
      await api.put(`/devices/${d.id}`, body);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const count = (s) => (data || []).filter((d) => d.status === s).length;
  return (
    <div className="fade-up">
      <PageHeader title="Device Health" subtitle="Live status of barriers, LPR, QR/RFID readers, payment terminals, network & power (SIMULATED adapters)" testid="devices-header">
        <Sel value={f.type} onChange={(v) => setF({ ...f, type: v })} testid="devices-type-filter" allLabel="All types" options={opts(TYPES)} />
        <Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="devices-status-filter" allLabel="All statuses" options={opts(["online", "offline", "fault", "maintenance"])} />
        <BranchSel value={branch} onChange={setBranch} testid="devices-branch" />
        {can("manage_devices") && <Button data-testid="device-add-btn" onClick={() => setAdd(true)}><Plus className="h-4 w-4" /> Device</Button>}
      </PageHeader>
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi testid="devices-online" label="Online" value={count("online")} tone="text-emerald-500" />
        <Kpi testid="devices-offline" label="Offline" value={count("offline")} alert={count("offline") > 0} />
        <Kpi testid="devices-fault" label="Fault" value={count("fault")} alert={count("fault") > 0} />
        <Kpi testid="devices-maintenance" label="Maintenance" value={count("maintenance")} tone="text-amber-500" />
      </div>
      <DataTable testid="devices-table" loading={loading} rows={data} columns={[
        { key: "device_code", label: "Device", render: (r) => <span className="flex items-center gap-2"><Dot ok={r.status === "online"} /><span className="font-mono text-xs font-bold">{r.device_code}</span></span> },
        { key: "branch", label: "Branch", render: (r) => branchName(r.branch_id) },
        { key: "type", label: "Type", render: (r) => label(r.type) },
        { key: "lane_name", label: "Lane" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.enabled ? r.status : "disabled"} /> },
        { key: "last_heartbeat", label: "Heartbeat", render: (r) => <span className="font-mono text-xs">{dt(r.last_heartbeat, { second: "2-digit" })}</span> },
        { key: "last_check", label: "Last check", render: (r) => dt(r.last_check) },
        { key: "fault", label: "Fault / incident", render: (r) => <span className="text-xs text-red-500">{r.fault || r.open_incident || ""}</span> },
        { key: "technician", label: "Technician" },
        { key: "maint", label: "Maint. last → next", render: (r) => <span className="font-mono text-xs">{(r.last_maintenance || "").slice(5, 10)} → {(r.next_maintenance || "").slice(5, 10)}</span> },
        { key: "act", label: "", render: (r) => (
          <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {can("manage_devices", "view_technical") && (
              <Sel value={r.status} onChange={(v) => upd(r, { status: v, fault: v === "fault" ? window.prompt("Fault description") || "Unspecified" : null }, `Status → ${v}`)}
                options={opts(["online", "offline", "fault", "maintenance"])} testid={`device-status-${r.device_code}`} className="h-8 w-32 text-xs" />
            )}
            {can("manage_devices") && <Switch checked={r.enabled} data-testid={`device-enable-${r.device_code}`} onCheckedChange={(v) => upd(r, { enabled: v }, v ? "Enabled" : "Disabled")} />}
            <button className="text-xs text-primary underline" data-testid={`device-logs-${r.device_code}`} onClick={() => setLogs(r.id)}>Logs</button>
          </span>
        ) },
      ]} />
      <DeviceLogs id={logs} onClose={() => setLogs(null)} />
      {add && <AddDevice onClose={() => setAdd(false)} onSaved={reload} />}
    </div>
  );
}
